import { useMemo, useRef, useState } from "react"
import escape from "lodash/escape"

import GrowingTextArea from "shared/growing_text_area"

import type { TextInputData, UIEvent } from "../types"
import { actionLabel, CardButton, CardShell } from "./card_shell"
import IngredientPicker from "./ingredient_picker"
import styles from "./text_input_card.module.scss"

interface Props {
  ui: UIEvent
  onRespond: (
    uiId: string,
    actionId: string,
    value: unknown,
    displayText?: string,
    ingredients?: Record<string, string>
  ) => void
}

// Public methods we use off the GrowingTextArea instance (it's an untyped JS
// class component).
interface FieldHandle {
  getCursorPosition: () => number
  setCursorPosition: (position: number) => void
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const TextInputCard = ({ ui, onRespond }: Props) => {
  const data = ui.data as TextInputData
  const groups = data.ingredient_groups ?? []

  const [answered, setAnswered] = useState(false)
  const [text, setText] = useState(data.current ?? "")
  const [focused, setFocused] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const fieldRef = useRef<FieldHandle | null>(null)

  // Maps each ingredient's filter code to its short display name, so the
  // blurred mirror can render {{filter_code}} tokens as pills.
  const nameByFilterCode = useMemo(() => {
    const map: Record<string, string> = {}
    groups.forEach(group => {
      group.ingredients.forEach(ingredient => {
        map[ingredient.filter_code] = ingredient.name
      })
    })
    return map
  }, [groups])

  // Same approach as the DIY IngredientTextField mirror: swap each known
  // {{filter_code}} token for an ingredient pill showing the short name.
  const renderPills = (html: string) =>
    Object.entries(nameByFilterCode).reduce(
      (acc, [filterCode, name]) =>
        acc.replace(
          new RegExp(`{{${escapeRegExp(filterCode)}}}`, "g"),
          `<span class="ingredient-pill">${escape(name)}</span>`
        ),
      html
    )

  const insertIngredient = (filterCode: string) => {
    if (answered) return
    const field = fieldRef.current
    const position = field?.getCursorPosition() ?? text.length
    const start = text.slice(0, position)
    const rest = text.slice(position)
    const token = (/\S$/.test(start) ? " " : "") + `{{${filterCode}}}`
    setText(`${start}${token}${rest}`)
    setPickerOpen(false)
    const cursor = position + token.length
    // Focusing the field also reveals the raw text again so it can be edited.
    window.setTimeout(() => field?.setCursorPosition(cursor))
  }

  const submit = () => {
    if (answered) return
    setAnswered(true)
    // The agent gets the raw text (tokens intact) as `value`; the displayed user
    // turn carries the same text plus the name map so it renders tokens as pills.
    onRespond(ui.id, "select", text, text.trim() ? text : "Leave this field blank", nameByFilterCode)
  }

  const dismiss = () => {
    if (answered) return
    setAnswered(true)
    onRespond(ui.id, "dismiss", undefined, actionLabel(ui.actions, "dismiss", "Not right now"))
  }

  return (
    <CardShell prompt={data.prompt} onDismiss={() => onRespond(ui.id, "close", undefined)}>
      <div className={styles["body"]}>
        <div className={`ingredients-text-field ${styles["field-wrapper"]}`}>
          <GrowingTextArea
            ref={fieldRef}
            multiline={data.multiline}
            minRows={data.multiline ? 3 : undefined}
            name="answer"
            ariaLabel="Your answer"
            placeholder="Enter text here"
            value={text}
            disabled={answered}
            showMirror={!focused}
            transformMirrorContent={renderPills}
            onChange={(value: string) => setText(value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>
        {data.helper_text ? <p className={styles["helper"]}>{data.helper_text}</p> : null}
        {pickerOpen && !answered ? (
          <IngredientPicker groups={groups} onSelect={insertIngredient} onClose={() => setPickerOpen(false)} />
        ) : null}
      </div>
      <div className={styles["footer"]}>
        {groups.length > 0 ? (
          <button
            type="button"
            className={styles["add-ingredient"]}
            disabled={answered}
            onClick={() => setPickerOpen(true)}
          >
            + Add ingredient
          </button>
        ) : null}
        <div className={styles["footer-actions"]}>
          <CardButton variant="secondary" disabled={answered} onClick={dismiss}>
            Cancel
          </CardButton>
          <CardButton variant="primary" disabled={answered} onClick={submit}>
            Save
          </CardButton>
        </div>
      </div>
    </CardShell>
  )
}

export default TextInputCard
